import React, { useEffect, useState } from "react";
import { Row, Col, Typography, Select, Input, Button, Table, Pagination, DatePicker, Spin, message } from "antd";
import { ReloadOutlined, SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import ExcelJS from "exceljs";
import SidebarLayout from "../BankerUI/SidebarLayout";
import _ChangeHistoryService from "../../modules/ChangeHistoryService";
import "../BankerUI/FirmAdminReports.scss";
import "./ChangeHistory.scss";

// Export row-count guardrail (requirement doc §12, open item #5) -- the client hasn't
// confirmed a specific limit yet, so this is a protective default pending that answer.
const MAX_EXPORT_EVENTS = 5000;

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MODULE_OPTIONS = [
  { value: "CreditFacilityTerms", label: "Credit Facility Terms" },
  { value: "CreditFacilityApplicant", label: "Credit Facility Applicant" },
  { value: "Valuations", label: "Valuations" },
  { value: "Balances", label: "Balances" },
  { value: "UserManagement", label: "User Management" },
  { value: "RoleManagement", label: "Role Management" },
  { value: "ApplicantRegister", label: "Applicant Register" },
  { value: "ApplicantUpdation", label: "Applicant Updation" },
  { value: "ApplicationStatus", label: "Application Status" },
  { value: "ApplicationStatusComment", label: "Application Status Comment" },
];

const ACTION_TYPE_OPTIONS = [
  { value: "Created", label: "Created" },
  { value: "Updated", label: "Updated" },
  { value: "Deleted", label: "Deleted" },
  { value: "Activated", label: "Activated" },
  { value: "Deactivated", label: "Deactivated" },
  { value: "RoleAssigned", label: "Role Assigned" },
  { value: "RoleRemoved", label: "Role Removed" },
];

const MODULE_LABELS = MODULE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});
const ACTION_TYPE_LABELS = ACTION_TYPE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

const ChangeHistory = () => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [dateRange, setDateRange] = useState(null);
  const [moduleName, setModuleName] = useState(undefined);
  const [actionType, setActionType] = useState(undefined);
  const [changedByName, setChangedByName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [detailByEventId, setDetailByEventId] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  const buildFilterPayload = (page, size) => ({
    startDate: dateRange?.[0] ? dateRange[0].startOf("day").toISOString() : null,
    endDate: dateRange?.[1] ? dateRange[1].endOf("day").toISOString() : null,
    moduleName: moduleName || null,
    actionType: actionType || null,
    changedByName: changedByName?.trim() || null,
    searchTerm: searchTerm?.trim() || null,
    pageIndex: page,
    pageSize: size,
    sortBy: "ChangeDate",
    sortAsc: false,
  });

  const fetchChangeHistory = async (page = pageIndex, size = pageSize) => {
    setLoading(true);
    try {
      const res = await _ChangeHistoryService.getChangeHistoryList(buildFilterPayload(page, size));

      if (res?.data?.IsSuccess && res.data.Obj) {
        setEvents(res.data.Obj.List || []);
        setTotalCount(res.data.Obj.TotalRecords || 0);
      } else {
        setEvents([]);
        setTotalCount(0);
        if (res?.data?.Message) {
          message.error(res.data.Message);
        }
      }
    } catch (error) {
      console.error("Error fetching change history:", error);
      message.error("Error fetching change history");
      setEvents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChangeHistory(pageIndex, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, dateRange, moduleName, actionType, changedByName, searchTerm]);

  const handlePaginationChange = (page, size) => {
    setPageIndex(page);
    setPageSize(size);
  };

  const resetFilters = () => {
    setDateRange(null);
    setModuleName(undefined);
    setActionType(undefined);
    setChangedByName("");
    setSearchTerm("");
    setPageIndex(1);
  };

  // Excel export: fetches every Change Event matching the current filters (not just the
  // current page), then the full field-level detail for each one, and flattens everything
  // into one row per changed field -- per requirement doc §9, exports must include the full
  // field-level detail, not just the summary row.
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const countRes = await _ChangeHistoryService.getChangeHistoryList(buildFilterPayload(1, 1));
      const totalMatching = countRes?.data?.Obj?.TotalRecords || 0;

      if (totalMatching === 0) {
        message.info("No change events match the current filters.");
        return;
      }
      if (totalMatching > MAX_EXPORT_EVENTS) {
        message.error(
          `Export is limited to ${MAX_EXPORT_EVENTS.toLocaleString()} change events. ` +
          `${totalMatching.toLocaleString()} match the current filters -- please narrow the date range or filters and try again.`
        );
        return;
      }

      const listRes = await _ChangeHistoryService.getChangeHistoryList(buildFilterPayload(1, totalMatching));
      const allEvents = listRes?.data?.Obj?.List || [];

      const details = await Promise.all(
        allEvents.map((evt) =>
          _ChangeHistoryService
            .getChangeHistoryDetail(evt.Id)
            .then((r) => (r?.data?.IsSuccess ? r.data.Obj : null))
            .catch(() => null)
        )
      );

      const HEADER_FILL = "FF2F6169";
      const THIN_BORDER = { style: "thin", color: { argb: "FF808080" } };
      const CELL_BORDER = { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Change History");

      worksheet.columns = [
        { width: 20 }, // Date/Time
        { width: 28 }, // Changed By
        { width: 18 }, // Module
        { width: 30 }, // Record
        { width: 14 }, // Action
        { width: 28 }, // Field
        { width: 28 }, // Before
        { width: 28 }, // After
      ];

      worksheet.mergeCells("A1:H1");
      const titleCell = worksheet.getCell("A1");
      titleCell.value = "Change History / Audit Log";
      titleCell.font = { bold: true, size: 13 };
      titleCell.alignment = { vertical: "middle" };
      worksheet.getRow(1).height = 22;

      worksheet.getCell("A2").value = `Exported: ${moment().format("MM/DD/YYYY hh:mm A")}`;
      worksheet.getCell("A2").font = { italic: true, size: 10 };

      const headerRowIndex = 4;
      const headerLabels = ["Date/Time", "Changed By", "Module", "Record", "Action", "Field", "Before", "After"];
      const headerRow = worksheet.getRow(headerRowIndex);
      headerLabels.forEach((label, idx) => {
        const cell = headerRow.getCell(idx + 1);
        cell.value = label;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
        cell.alignment = { vertical: "middle", horizontal: "left" };
        cell.border = CELL_BORDER;
      });
      headerRow.height = 20;
      headerRow.commit();

      let currentRowIndex = headerRowIndex + 1;

      const writeRow = (baseValues, field, before, after) => {
        const row = worksheet.getRow(currentRowIndex);
        [...baseValues, field, before, after].forEach((val, idx) => {
          const cell = row.getCell(idx + 1);
          cell.value = val ?? "";
          cell.border = CELL_BORDER;
          cell.alignment = { horizontal: "left" };
        });
        row.commit();
        currentRowIndex += 1;
      };

      allEvents.forEach((evt, idx) => {
        const detail = details[idx];
        const baseValues = [
          evt.ChangeDate ? moment(evt.ChangeDate).format("MM/DD/YYYY hh:mm A") : "-",
          `${evt.ChangedByName || ""}${evt.ChangedByRoleName ? ` (${evt.ChangedByRoleName})` : ""}`,
          MODULE_LABELS[evt.ModuleName] || evt.ModuleName,
          evt.RecordReference,
          ACTION_TYPE_LABELS[evt.ActionType] || evt.ActionType,
        ];

        if (detail?.FieldChanges?.length) {
          detail.FieldChanges.forEach((fc) => {
            writeRow(baseValues, fc.FieldName, fc.OldValue, fc.NewValue);
          });
          return;
        }

        if (detail?.RecordSnapshotJson) {
          let parsed = {};
          try {
            parsed = JSON.parse(detail.RecordSnapshotJson);
          } catch (error) {
            console.error("Error parsing snapshot JSON during export:", error);
          }
          const entries = Object.entries(parsed).filter(([, value]) => value !== null && value !== undefined && value !== "");
          if (entries.length) {
            entries.forEach(([key, value]) => writeRow(baseValues, key, "", String(value)));
            return;
          }
        }

        writeRow(baseValues, "-", "", "");
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Levridge_ChangeHistory_Export_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success("Change history exported successfully.");
    } catch (error) {
      console.error("Error exporting change history:", error);
      message.error("Error exporting change history");
    } finally {
      setExportLoading(false);
    }
  };

  // Drill-down: lazily fetches full field-level detail (or the Created/Deleted snapshot)
  // the first time a row is expanded, then caches it per event id.
  const handleExpand = async (expanded, record) => {
    if (!expanded) {
      setExpandedRowKeys((prev) => prev.filter((k) => k !== record.Id));
      return;
    }

    setExpandedRowKeys((prev) => (prev.includes(record.Id) ? prev : [...prev, record.Id]));

    if (detailByEventId[record.Id]) {
      return;
    }

    setDetailByEventId((prev) => ({ ...prev, [record.Id]: { loading: true } }));
    try {
      const res = await _ChangeHistoryService.getChangeHistoryDetail(record.Id);
      if (res?.data?.IsSuccess && res.data.Obj) {
        setDetailByEventId((prev) => ({ ...prev, [record.Id]: { loading: false, data: res.data.Obj } }));
      } else {
        setDetailByEventId((prev) => ({
          ...prev,
          [record.Id]: { loading: false, error: res?.data?.Message || "Failed to load details" },
        }));
      }
    } catch (error) {
      console.error("Error fetching change history detail:", error);
      setDetailByEventId((prev) => ({ ...prev, [record.Id]: { loading: false, error: "Error fetching details" } }));
    }
  };

  const detailColumns = [
    { title: "Field", dataIndex: "FieldName", key: "field" },
    { title: "Before", dataIndex: "OldValue", key: "before" },
    { title: "After", dataIndex: "NewValue", key: "after" },
  ];

  const snapshotColumns = [
    { title: "Field", dataIndex: "key", key: "field" },
    { title: "Value", dataIndex: "value", key: "value" },
  ];

  const expandedRowRender = (record) => {
    const state = detailByEventId[record.Id];

    if (!state || state.loading) {
      return <Spin size="small" />;
    }
    if (state.error) {
      return <span className="change-history-detail-error">{state.error}</span>;
    }

    const detail = state.data;

    // Updated actions: field-by-field before/after, per requirement doc §5.3.
    if (detail.FieldChanges && detail.FieldChanges.length > 0) {
      return (
        <Table
          rowKey={(_, index) => index}
          columns={detailColumns}
          dataSource={detail.FieldChanges}
          pagination={false}
          size="small"
          className="transparent-table change-history-detail-table"
        />
      );
    }

    // Created/Deleted actions: whole-record snapshot instead of a field list, per §5.4.
    if (detail.RecordSnapshotJson) {
      let snapshotRows = [];
      try {
        const parsed = JSON.parse(detail.RecordSnapshotJson);
        snapshotRows = Object.entries(parsed)
          .filter(([, value]) => value !== null && value !== undefined && value !== "")
          .map(([key, value]) => ({ key, value: String(value) }));
      } catch (error) {
        console.error("Error parsing snapshot JSON:", error);
      }
      return (
        <Table
          rowKey="key"
          columns={snapshotColumns}
          dataSource={snapshotRows}
          pagination={false}
          size="small"
          className="transparent-table change-history-detail-table"
        />
      );
    }

    return <span>No further detail available.</span>;
  };

  const columns = [
    {
      title: "Date/Time",
      dataIndex: "ChangeDate",
      key: "changeDate",
      align: "left",
      render: (value) => (value ? moment(value).format("MM/DD/YYYY hh:mm A") : "-"),
    },
    {
      title: "Changed By",
      key: "changedBy",
      align: "left",
      render: (_, record) => (
        <span>
          {record.ChangedByName}
          {record.ChangedByRoleName ? <span className="changed-by-role"> ({record.ChangedByRoleName})</span> : null}
        </span>
      ),
    },
    {
      title: "Module",
      dataIndex: "ModuleName",
      key: "moduleName",
      align: "left",
      render: (value) => MODULE_LABELS[value] || value,
    },
    {
      title: "Record",
      dataIndex: "RecordReference",
      key: "recordReference",
      align: "left",
    },
    {
      title: "Action",
      dataIndex: "ActionType",
      key: "actionType",
      align: "left",
      render: (value) => ACTION_TYPE_LABELS[value] || value,
    },
    {
      title: "Fields Changed",
      dataIndex: "FieldsChangedSummary",
      key: "fieldsChangedSummary",
      align: "left",
    },
  ];

  return (
    <Spin spinning={loading}>
      <SidebarLayout>
        <div className="change-history-page" style={{ margin: "0 auto", padding: 20 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ marginBottom: 0 }}>
                Change History
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                onClick={handleExportExcel}
              >
                Export to Excel
              </Button>
            </Col>
          </Row>

          <div className="compliance-filter-card">
            <div className="card-title">Filters</div>
            <Row gutter={[12, 12]} align="bottom">
              <Col xs={24} md={8} lg={6}>
                <div className="field-label">Date Range</div>
                <RangePicker
                  value={dateRange}
                  onChange={(value) => {
                    setDateRange(value);
                    setPageIndex(1);
                  }}
                  className="reports-select change-history-range-picker"
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} md={8} lg={5}>
                <div className="field-label">Module</div>
                <Select
                  value={moduleName}
                  onChange={(value) => {
                    setModuleName(value);
                    setPageIndex(1);
                  }}
                  placeholder="All Modules"
                  allowClear
                  className="reports-select"
                  style={{ width: "100%" }}
                >
                  {MODULE_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={8} lg={5}>
                <div className="field-label">Action Type</div>
                <Select
                  value={actionType}
                  onChange={(value) => {
                    setActionType(value);
                    setPageIndex(1);
                  }}
                  placeholder="All Actions"
                  allowClear
                  className="reports-select"
                  style={{ width: "100%" }}
                >
                  {ACTION_TYPE_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={4}>
                <div className="field-label">Changed By</div>
                <Input
                  value={changedByName}
                  onChange={(e) => {
                    setChangedByName(e.target.value);
                    setPageIndex(1);
                  }}
                  placeholder="User name"
                  allowClear
                  className="reports-input"
                />
              </Col>
              <Col xs={24} md={12} lg={4}>
                <div className="field-label">Search</div>
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPageIndex(1);
                  }}
                  placeholder="Record or field name"
                  allowClear
                  prefix={<SearchOutlined />}
                  className="reports-input"
                />
              </Col>
              <Col>
                <Button icon={<ReloadOutlined />} onClick={resetFilters} className="reset-filter-btn">
                  Reset Filters
                </Button>
              </Col>
            </Row>
          </div>

          <Table
            rowKey={(record) => record.Id}
            columns={columns}
            dataSource={events}
            pagination={false}
            style={{ marginTop: 20 }}
            className="transparent-table change-history-table"
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpand: handleExpand,
            }}
            onRow={(record) => ({
              onClick: (e) => {
                // The built-in expand icon already triggers `expandable.onExpand` on its own
                // click; without this guard, this row-level handler would fire a second,
                // opposite toggle right after it and immediately collapse what was just expanded.
                if (e.target.closest && e.target.closest(".ant-table-row-expand-icon")) {
                  return;
                }
                handleExpand(!expandedRowKeys.includes(record.Id), record);
              },
            })}
          />
          <Pagination
            className="pagination"
            showSizeChanger
            current={pageIndex}
            pageSize={pageSize}
            total={totalCount}
            onChange={handlePaginationChange}
            style={{ marginTop: 16, textAlign: "right" }}
          />
        </div>
      </SidebarLayout>
    </Spin>
  );
};

export default ChangeHistory;
